'use client'

import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import AContentCardAddAdditional from '@/_components/custom/cards/content-card/components/a-content-card-add-additional'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/_components/ui/select'
import { formatPrice } from '@/_lib/utils'
import { IAnnualProgram, IAPCategory } from '@/_types'
import { pushAddToCart } from '@/_utils/analytics/datalayer'

interface IProps {
	annualPrograms: IAPCategory[]
}

interface IAnnualProgramWithName extends IAnnualProgram {
	_name: string
	_nameShort: string
}

interface IFamilySelectProps {
	name: string
	options: IAnnualProgramWithName[] | undefined
	placeholder: string
	onSelect: (value: string) => void
	selectedValue: string
	error?: string
}

const ASelect = ({ options, placeholder, onSelect, selectedValue, error }: IFamilySelectProps) => {
	if (!options) return null

	return (
		<div>
			<Select onValueChange={value => onSelect(value)} value={selectedValue}>
				<SelectTrigger
					name='Select'
					className='border text-medante-purple-dark border-medante-purple-dark rounded-xl lg:rounded-3xl min-h-[52px] lg:min-h-[65px] px-6'
				>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent className='bg2 px-6'>
					{options &&
						options.length > 0 &&
						options?.map(option => (
							<SelectItem
								key={option.id}
								value={option.id.toString()}
								className='w-full text-sm py-3 text-medante-purple-dark font-medium rounded-xl px-4 focus:bg-medante-purple-dark focus:text-white hover:bg-medante-purple-dark/10'
							>
								<span className='hidden lg:block'>{option._name}</span>
								<span className='block lg:hidden'>{option._nameShort}</span>
							</SelectItem>
						))}
				</SelectContent>
			</Select>
			{error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
		</div>
	)
}

const AAPCategoryFamilySelect = ({ annualPrograms }: IProps) => {
	const router = useRouter()
	const t = useTranslations('pages.apCategories')
	const [selectCount, setSelectCount] = useState(2)
	const [selectedPrograms, setSelectedPrograms] = useState<string[]>([])
	const [selectedModulesPerProgram, setSelectedModulesPerProgram] = useState<number[][]>([[], []])
	const [isSubmitted, setIsSubmitted] = useState(false)
	const addToCartEmittedIndicesRef = useRef<Set<number>>(new Set())

	useEffect(() => {
		setSelectedModulesPerProgram(prev => {
			if (prev.length === selectCount) return prev
			const next = [...prev]
			while (next.length < selectCount) next.push([])
			next.length = selectCount
			return next
		})
	}, [selectCount])

	const allOptions = useMemo(
		() =>
			annualPrograms
				.map(program =>
					program.yearPrograms.map(p => ({
						...p,
						_name: `${program.name} - ${p.name}`,
						_nameShort: `${
							program.nameShort && program.nameShort.length > 0
								? program.nameShort
								: program.name
						} - ${p.name}`
					}))
				)
				.flat()
				.filter(
					program =>
						!program?.name?.toLowerCase()?.includes('mini') &&
						!program?.name?.toLowerCase()?.includes('rodina') &&
						!program?.name?.toLowerCase()?.includes('gynekológ')
				) as IAnnualProgramWithName[],
		[annualPrograms]
	)

	const addNewSelect = () => {
		if (selectCount < 5) {
			setSelectCount(prev => prev + 1)
		}
	}

	const removeSelect = (index: number) => {
		if (selectCount > 2) {
			setSelectCount(prev => prev - 1)
			const newSelectedPrograms = [...selectedPrograms]
			newSelectedPrograms.splice(index, 1)
			setSelectedPrograms(newSelectedPrograms)

			const newModules = [...selectedModulesPerProgram]
			newModules.splice(index, 1)
			setSelectedModulesPerProgram(newModules)
		}
	}

	const handleSelectChange = (index: number, value: string) => {
		const newSelectedPrograms = [...selectedPrograms]
		newSelectedPrograms[index] = value
		setSelectedPrograms(newSelectedPrograms)

		setSelectedModulesPerProgram(prev => {
			const next = [...prev]
			next[index] = []
			return next
		})
	}

	const handleModulesChange = (index: number, module: { id: number }, isChecked: boolean) => {
		setSelectedModulesPerProgram(prev => {
			const next = [...prev]
			const cur = [...(next[index] ?? [])]
			next[index] = isChecked
				? cur.includes(module.id)
					? cur
					: [...cur, module.id]
				: cur.filter(id => id !== module.id)
			return next
		})
	}

	const finalPrice = useMemo(() => {
		let total = 0

		selectedPrograms.forEach((program, index) => {
			const programData = allOptions?.find(p => p.id.toString() === program)
			if (programData) {
				total += (programData.priceOld || programData.price) * 0.9

				const programModules = selectedModulesPerProgram[index] ?? []
				programModules.forEach(moduleId => {
					const moduleData = programData.modules?.find(m => m.id === moduleId)
					if (moduleData) total += moduleData.price
				})
			}
		})

		return total
	}, [selectedPrograms, allOptions, selectedModulesPerProgram])

	const emitFamilyAddToCartForIndex = useCallback(
		(index: number) => {
			if (index < 0 || addToCartEmittedIndicesRef.current.has(index)) return

			const programIdStr = selectedPrograms[index]
			if (!programIdStr) return

			const programData = allOptions.find(p => p.id.toString() === programIdStr)
			if (!programData) return

			const listPrice = programData.priceOld || programData.price
			const discountedPrice = listPrice * 0.9

			pushAddToCart(
				{
					id: programData.id,
					name: programData.name,
					type: 'annual-program-family',
					price: discountedPrice,
					priceOld: listPrice
				},
				{ item_category2: 'Rodina', coupon: 'rodina-10', value: discountedPrice }
			)

			addToCartEmittedIndicesRef.current.add(index)
		},
		[allOptions, selectedPrograms]
	)

	useEffect(() => {
		selectedPrograms.forEach((programIdStr, index) => {
			if (!programIdStr) return
			if (addToCartEmittedIndicesRef.current.has(index)) return
			emitFamilyAddToCartForIndex(index)
		})
	}, [selectedPrograms, emitFamilyAddToCartForIndex])

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		setIsSubmitted(true)

		if (
			selectedPrograms.length === 0 ||
			selectedPrograms.some(program => !program) ||
			selectedPrograms.length < 2
		) {
			return
		}

		const queryParams = new URLSearchParams()
		const validPrograms = selectedPrograms.filter(program => program)
		queryParams.append('programs', validPrograms.join(','))

		const hasAnyModules = selectedModulesPerProgram.some(modules => modules?.length > 0)
		if (hasAnyModules) {
			const modulesParam = validPrograms
				.map((_, i) => (selectedModulesPerProgram[i] ?? []).join(','))
				.join('|')
			queryParams.append('modulesPerProgram', modulesParam)
		}

		router.push(`/volne-terminy?type=annual-program-family&${queryParams.toString()}`)
	}

	return (
		<div className='anim_delay anim_drop1 observe'>
			<form name='family-programs-form' onSubmit={onSubmit}>
				<div className='boomex-grid align-start mgt-20 gap-y-4 lg:gap-y-8'>
					{Array.from({ length: selectCount }, (_, index) => {
						const selectedProgramData = allOptions.find(
							p => p.id.toString() === selectedPrograms[index]
						)

						return (
							<div key={index} className='col3 col1_lgm relative h-full'>
								<ASelect
									name={`programs-${index}`}
									options={allOptions}
									placeholder={`Balík ${index + 1}`}
									onSelect={value => handleSelectChange(index, value)}
									selectedValue={selectedPrograms[index] || ''}
									error={
										isSubmitted && !selectedPrograms[index]
											? `Vyberte balík ${index + 1}`
											: undefined
									}
								/>
								{selectedProgramData && (
									<AContentCardAddAdditional
										data={selectedProgramData}
										className='item_modul mt-3'
										onModulesChange={(mod, checked) =>
											handleModulesChange(index, mod, checked)
										}
										selectedModuleIds={selectedModulesPerProgram[index] ?? []}
									/>
								)}
								{selectCount > 2 && (
									<div
										className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-medante-pink-secondary hover:bg-medante-pink cursor-pointer flex items-center justify-center text-white text-sm font-bold transition-all duration-200'
										onClick={() => removeSelect(index)}
									>
										-
									</div>
								)}
							</div>
						)
					})}
					{selectCount < 5 && (
						<div
							className='col3 col1_lgm  flex items-center justify-center lg:!justify-start h-full pt-2'
							onClick={addNewSelect}
						>
							<div className='w-10 h-10 rounded-full  cursor-pointer flex items-center bg-medante-purple-dark justify-center text-xl font-semibold text-white transition-all  hover:bg-medante-purple-light hover:text-white duration-300'>
								+
							</div>
						</div>
					)}
				</div>
				<div className='col1 dflex justify-center mgt-50'>
					<button type='submit' className='btn_basic type6'>
						<span>
							{t('family_programs.familySelectButton', {
								price: formatPrice(finalPrice, {
									omitZeroFraction: true
								}).price
							})}
						</span>
						<span className=''>
							<span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar3_r' />
								</svg>
							</span>
							<span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar3_r' />
								</svg>
							</span>
						</span>
					</button>
				</div>
			</form>
		</div>
	)
}

export default AAPCategoryFamilySelect
